import { getApiBaseUrl, getMerchantClient } from '@beatq/sdk-client';
import {
  getAccessToken,
  setAccessToken,
  mapErrorCode,
  readDataFromEnvelope,
  readEntityFromEnvelope,
  readObjectFieldFromEnvelope,
} from '@beatq/shared';
import type { FieldErrors } from './useFormErrors.js';
import {
  ONBOARDING_CHECKLIST_STEPS,
  COMPANY_NUMBER_REQUIRED_ENTITY_TYPES,
  CHECKLIST_STEP_ESTIMATE_MINUTES,
  MERCHANT_ONBOARDING_SUPPORT_EMAIL,
  ONBOARDING_POLL_INTERVAL_MS,
} from './catalog.js';

type ApiErr = { error?: { code: string; message: string } };

export function sdkErrorMessage(error: unknown): string {
  const e = error as ApiErr;
  return e?.error?.message ?? mapErrorCode(e?.error?.code ?? 'ERR-UNKNOWN');
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function asString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

export function throwMerchantSdkError(error: unknown): never {
  const rec = asRecord(error);
  const nested = asRecord(rec?.error);
  const err = new Error(sdkErrorMessage(error)) as Error & {
    code?: string;
    stepUp?: unknown;
    error?: unknown;
  };
  err.code = asString(nested?.code) ?? asString(rec?.code) ?? undefined;
  err.stepUp =
    nested?.stepUp ??
    asRecord(nested?.details)?.stepUp ??
    rec?.stepUp ??
    asRecord(rec?.details)?.stepUp;
  err.error = rec?.error ?? rec;
  throw err;
}

/** 10-03 ERR-AUTH-0051 body includes `stepUp.challengeToken`. */
export function extractStepUpChallenge(error: unknown): string | null {
  const visit = (value: unknown, depth = 0): string | null => {
    if (value == null || depth > 6) return null;
    const rec = asRecord(value);
    if (!rec) return null;
    const direct = asString(rec.challengeToken) ?? asString(asRecord(rec.stepUp)?.challengeToken);
    if (direct) return direct;
    for (const key of ['error', 'stepUp', 'details'] as const) {
      const nested = rec[key];
      if (Array.isArray(nested)) {
        for (const item of nested) {
          const found = visit(item, depth + 1);
          if (found) return found;
        }
      } else {
        const found = visit(nested, depth + 1);
        if (found) return found;
      }
    }
    return null;
  };
  if (error instanceof Error) {
    const extra = error as Error & { stepUp?: unknown; error?: unknown };
    return visit(extra) ?? visit(extra.stepUp) ?? visit(extra.error);
  }
  return visit(error);
}

export async function verifyMerchantMfa(code: string, challengeToken: string) {
  const client = getMerchantClient();
  const { data, error, response } = await client.POST('/merchant/v1/auth/mfa/verify' as never, {
    body: { code, challengeToken } as never,
  });
  if (!response.ok || error) throwMerchantSdkError(error);
  const rec = asRecord(data);
  const inner = asRecord(rec?.data) ?? rec;
  const tokens = asRecord(inner?.tokens);
  const accessToken =
    asString(tokens?.accessToken) ?? asString(inner?.accessToken) ?? asString(rec?.accessToken);
  if (accessToken) setAccessToken(accessToken);
  return data;
}

export type MerchantMfaEnrolment = {
  enrolmentId?: string;
  secret?: string;
  otpauthUrl?: string;
};

export async function enrolMerchantMfa(): Promise<MerchantMfaEnrolment> {
  const client = getMerchantClient();
  const { data, error, response } = await client.POST('/merchant/v1/auth/mfa/enrol' as never, {
    body: { method: 'TOTP', label: 'Merchant owner' } as never,
  });
  if (!response.ok || error) throwMerchantSdkError(error);
  return readDataFromEnvelope<MerchantMfaEnrolment>(data) ?? {};
}

export async function confirmMerchantMfa(enrolmentId: string, code: string): Promise<string[]> {
  const client = getMerchantClient();
  const { data, error, response } = await client.POST('/merchant/v1/auth/mfa/confirm' as never, {
    body: { enrolmentId, code } as never,
  });
  if (!response.ok || error) throwMerchantSdkError(error);
  return (
    readDataFromEnvelope<{ recoveryCodes?: string[] }>(data)?.recoveryCodes?.filter(Boolean) ?? []
  );
}

/**
 * Maps raw API errors from merchant registration into actionable guidance.
 * The backend deliberately uses vague messages like "Registration could not be
 * completed" to avoid leaking account-existence info — but a merchant needs
 * clear direction on what to do next.
 */
export function humanizeSignUpError(
  error: unknown,
  email?: string,
): { title: string; body: string; showSignIn: boolean } {
  const message = (error instanceof Error ? error.message : String(error)).toLowerCase();
  const apiErr = error as { code?: string };
  const code = (apiErr.code ?? '').toUpperCase();

  if (
    message.includes('could not be completed') ||
    message.includes('already') ||
    code === 'ERR-VALD-1000' ||
    code.startsWith('ERR-AUTH-')
  ) {
    const hint = email ? ` (${email})` : '';
    return {
      title: 'This email may already be registered',
      body:
        `An account with this email${hint} may already exist. ` +
        'Try signing in instead, or use a different email address. ' +
        `If you need help, contact ${MERCHANT_ONBOARDING_SUPPORT_EMAIL}.`,
      showSignIn: true,
    };
  }

  if (message.includes('password') || code === 'ERR-VALD-1002') {
    return {
      title: 'Password does not meet requirements',
      body: 'Your password must be at least 8 characters. Choose a stronger password and try again.',
      showSignIn: false,
    };
  }

  if (message.includes('email') || code === 'ERR-VALD-1001') {
    return {
      title: 'Invalid email address',
      body: 'Please check your email address is entered correctly (e.g. name@example.com).',
      showSignIn: false,
    };
  }

  if (
    message.includes('cannot reach') ||
    message.includes('network') ||
    message.includes('fetch')
  ) {
    return {
      title: 'Connection problem',
      body: 'We could not reach our servers. Please check your internet connection and try again.',
      showSignIn: false,
    };
  }

  return {
    title: 'Something went wrong',
    body:
      `We were unable to create your account. Please try again. ` +
      `If the problem persists, contact ${MERCHANT_ONBOARDING_SUPPORT_EMAIL} for assistance.`,
    showSignIn: false,
  };
}

/** Client-side guard before MOD-005 submit — mirrors BR-VAL-018 for LIMITED companies. */
export function validateDraftBusinessDetails(input: {
  legalName?: string;
  tradingName?: string;
  billingEmail?: string;
  companyNumber?: string;
  entityType?: string;
}): string | null {
  if (!input.tradingName?.trim()) return 'Trading name is required';
  if (!input.billingEmail?.trim()) return 'Billing email is required';
  if (!input.legalName?.trim() && !input.tradingName?.trim()) return 'Legal name is required';

  const entityType = (input.entityType ?? 'LIMITED').trim().toUpperCase();
  if (!COMPANY_NUMBER_REQUIRED_ENTITY_TYPES.has(entityType)) return null;

  const normalized = input.companyNumber?.replace(/\s/g, '').toUpperCase();
  if (!normalized || !/^(\d{8}|[A-Z]{2}\d{6})$/.test(normalized)) {
    return 'Enter a valid company number (8 digits, or 2 letters and 6 digits, e.g. 12345678)';
  }
  return null;
}

// ─── Multi-error field validators (04-02 Completeness rule) ──────────

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const UK_POSTCODE_PATTERN = /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i;
const VAT_PATTERN = /^GB\d{9}(\d{3})?$/i;

/** BR-VAL-001/002/004/010/016 — all signup fields at once. */
export function validateSignUpFields(input: {
  tradingName: string;
  contactName: string;
  billingEmail: string;
  password: string;
  postcode: string;
}): FieldErrors {
  const errors: FieldErrors = {};
  const trading = input.tradingName.trim();
  if (!trading) {
    errors.tradingName = 'Business name is required';
  } else if (trading.length < 2 || trading.length > 120) {
    errors.tradingName = 'Business name must be between 2 and 120 characters';
  }

  const contact = input.contactName.trim();
  if (!contact) {
    errors.contactName = 'Contact name is required';
  } else if (contact.length > 70) {
    errors.contactName = 'Contact name must be 70 characters or fewer';
  }

  const email = input.billingEmail.trim();
  if (!email) {
    errors.billingEmail = 'Email address is required';
  } else if (!EMAIL_PATTERN.test(email) || email.length > 254) {
    errors.billingEmail = 'Enter a valid email address';
  }

  if (!input.password) {
    errors.password = 'Password is required';
  } else if (input.password.length < 8) {
    errors.password = 'Password must be at least 8 characters';
  }

  const postcode = input.postcode.trim();
  if (!postcode) {
    errors.postcode = 'Postcode is required';
  } else if (!UK_POSTCODE_PATTERN.test(postcode)) {
    errors.postcode = 'Enter a valid UK postcode (e.g. SW1A 1AA)';
  }

  return errors;
}

/** BR-VAL-016/017/018/019/010 — business details before submit. */
export function validateBusinessDetailsFields(input: {
  legalName: string;
  tradingName: string;
  billingEmail: string;
  companyNumber: string;
  entityType: string;
  vatNumber?: string;
  registeredAddressLine1?: string;
  registeredPostcode?: string;
  tradingAddressLine1?: string;
  tradingPostcode?: string;
  sameTradingAddress?: boolean;
}): FieldErrors {
  const errors: FieldErrors = {};

  const trading = input.tradingName.trim();
  if (!trading) {
    errors.tradingName = 'Trading name is required';
  } else if (trading.length < 2 || trading.length > 120) {
    errors.tradingName = 'Trading name must be between 2 and 120 characters';
  }

  const email = input.billingEmail.trim();
  if (!email) {
    errors.billingEmail = 'Billing email is required';
  } else if (!EMAIL_PATTERN.test(email) || email.length > 254) {
    errors.billingEmail = 'Enter a valid email address';
  }

  const legal = input.legalName.trim();
  if (!legal && !trading) {
    errors.legalName = 'Legal name is required';
  }

  const entityType = (input.entityType ?? 'LIMITED').trim().toUpperCase();
  if (COMPANY_NUMBER_REQUIRED_ENTITY_TYPES.has(entityType)) {
    const normalized = input.companyNumber?.replace(/\s/g, '').toUpperCase();
    if (!normalized || !/^(\d{8}|[A-Z]{2}\d{6})$/.test(normalized)) {
      errors.companyNumber =
        'Enter a valid company number (8 digits, or 2 letters and 6 digits, e.g. 12345678)';
    }
  }

  const vat = input.vatNumber?.replace(/\s/g, '').toUpperCase();
  if (vat && !VAT_PATTERN.test(vat)) {
    errors.vatNumber = 'Enter a valid UK VAT number (e.g. GB123456789)';
  }

  if (input.registeredAddressLine1 !== undefined) {
    if (!input.registeredAddressLine1.trim()) {
      errors.registeredAddressLine1 = 'Registered address is required';
    }
  }

  if (input.registeredPostcode !== undefined) {
    const rp = input.registeredPostcode.trim();
    if (!rp) {
      errors.registeredPostcode = 'Registered postcode is required';
    } else if (!UK_POSTCODE_PATTERN.test(rp)) {
      errors.registeredPostcode = 'Enter a valid UK postcode';
    }
  }

  if (input.sameTradingAddress === false) {
    if (input.tradingAddressLine1 !== undefined && !input.tradingAddressLine1.trim()) {
      errors.tradingAddressLine1 = 'Trading address is required';
    }
    if (input.tradingPostcode !== undefined) {
      const tp = input.tradingPostcode.trim();
      if (!tp) {
        errors.tradingPostcode = 'Trading postcode is required';
      } else if (!UK_POSTCODE_PATTERN.test(tp)) {
        errors.tradingPostcode = 'Enter a valid UK postcode';
      }
    }
  }

  return errors;
}

/** Maps MOD-005 stepKey → FE checklist step code */
const STEP_KEY_TO_CHECKLIST: Record<string, string> = {
  BUSINESS_DETAILS: 'business-details',
  VERIFICATION: 'beat-pay-setup',
  TERMS: 'commercial-terms',
  ACCOUNT: 'business-details',
  VERTICAL: 'business-details',
  INTENT: 'business-details',
};

/** Primary MOD-005 step that drives each checklist row (avoids duplicate-key collisions). */
const CHECKLIST_PRIMARY_STEP_KEY: Partial<Record<string, string>> = {
  'business-details': 'BUSINESS_DETAILS',
  'commercial-terms': 'TERMS',
};

const DRAFT_LOCKED_CHECKLIST_CODES = new Set([
  'beat-pay-setup',
  'create-branch',
  'opening-hours',
  'service-area',
  'fulfilment-settings',
  'catalogue',
  'go-live',
]);

export type OnboardingApplication = {
  applicationId?: string;
  resumeToken?: string;
  status?: string;
  /** Present once submitted — the tenant the rest of onboarding runs against. */
  businessId?: string;
  draftPayload?: Record<string, unknown>;
  steps?: Array<{
    stepKey?: string;
    code?: string;
    label?: string;
    status?: string;
    blockers?: string[];
  }>;
  estimatedGoLiveAt?: string | null;
  blockingReasons?: Array<{ stepKey?: string; reason?: string }>;
};

export type MerchantBusiness = {
  id?: string;
  legalName?: string;
  tradingName?: string;
  billingEmail?: string;
  status?: string;
  postcode?: string | null;
  companyNumber?: string | null;
  description?: string | null;
  supportPhoneE164?: string | null;
  vatNumber?: string | null;
  vatRegistered?: boolean;
  entityType?: string | null;
  registeredAddressLine1?: string | null;
  registeredPostcode?: string | null;
  tradingAddressLine1?: string | null;
  tradingPostcode?: string | null;
};

export type CommercialTermsVersion = {
  id?: string;
  version?: string;
  commissionBps?: number;
  serviceFeeRateBps?: number;
  instantPayoutFeeMinor?: number;
  smallOrderFeeMinor?: number;
  refundHandling?: string;
  commissionModel?: string;
  effectiveFrom?: string;
  acceptBy?: string | null;
  pending?: boolean;
  acceptedAt?: string | null;
};

export type ChecklistStepStatus =
  | 'not_started'
  | 'in_progress'
  | 'submitted'
  | 'blocked'
  | 'waiting_on_us'
  | 'needs-attention'
  | 'complete';

export type ChecklistStepProgress = {
  code: string;
  label: string;
  route: string;
  status: ChecklistStepStatus;
  blockers: string[];
  estimateMinutes?: number;
};

/** Maps MOD-005 backend step status to 07-05 checklist vocabulary. */
export function normalizeBackendChecklistStatus(
  backendStatus?: string,
  blockers: string[] = [],
): ChecklistStepStatus {
  if (backendStatus === 'COMPLETED') return 'complete';
  if (blockers.length > 0) return 'blocked';
  if (backendStatus === 'IN_PROGRESS') return 'in_progress';
  return 'not_started';
}

/** Prefer tenant checklist derivation once MOD-005 has bound a business. */
export function shouldUseTenantChecklistEngine(
  applicationId: string | null | undefined,
  application?: OnboardingApplication,
): boolean {
  return !applicationId || Boolean(application?.businessId);
}

function resolveApplicationStepStatus(
  stepCode: string,
  application: OnboardingApplication | undefined,
  backendStatus: string | undefined,
  blockers: string[],
): ChecklistStepStatus {
  if (backendStatus === 'COMPLETED') return 'complete';
  const actionableBlockers = blockers.filter((reason) => !/^step incomplete$/i.test(reason));
  if (actionableBlockers.length > 0) return 'blocked';

  if (stepCode === 'business-details') {
    const draft = application?.draftPayload ?? {};
    const hasPartial = Boolean(draft.tradingName && draft.billingEmail);
    if (hasPartial) return 'in_progress';
  }

  return normalizeBackendChecklistStatus(backendStatus, actionableBlockers);
}

export function checklistStepStatusLabel(status: ChecklistStepStatus | 'locked'): string {
  switch (status) {
    case 'complete':
      return 'Complete';
    case 'in_progress':
      return 'In progress';
    case 'submitted':
      return 'Submitted';
    case 'blocked':
      return 'Blocked';
    case 'needs-attention':
      return 'Needs attention';
    case 'locked':
      return 'Locked';
    case 'waiting_on_us':
      return 'Waiting on us';
    default:
      return 'Not started';
  }
}

export function pickNextChecklistStep(
  steps: ChecklistStepProgress[],
): ChecklistStepProgress | undefined {
  return steps.find(
    (step) =>
      step.status !== 'complete' && step.status !== 'waiting_on_us' && step.code !== 'go-live',
  );
}

export function checklistStepCodeFromPath(pathname: string): string | null {
  const match = ONBOARDING_CHECKLIST_STEPS.find(
    (step) => pathname === step.route || pathname.startsWith(`${step.route}?`),
  );
  return match?.code ?? null;
}

/** Current, already-started, or waiting-on-us steps. Later locked rows stay closed. */
export function isChecklistStepReachable(steps: ChecklistStepProgress[], code: string): boolean {
  const index = steps.findIndex((step) => step.code === code);
  if (index < 0) return true;
  const next = pickNextChecklistStep(steps);
  if (next?.code === code) return true;
  const step = steps[index];
  if (
    step.status === 'complete' ||
    step.status === 'in_progress' ||
    step.status === 'submitted' ||
    step.status === 'waiting_on_us'
  ) {
    return true;
  }
  return steps
    .slice(0, index)
    .every((row) => row.status === 'complete' || row.status === 'waiting_on_us');
}

export function previousChecklistStep(
  steps: ChecklistStepProgress[],
  currentCode: string,
): ChecklistStepProgress | undefined {
  const index = steps.findIndex((step) => step.code === currentCode);
  if (index <= 0) return undefined;
  return [...steps.slice(0, index)]
    .reverse()
    .find((step) => isChecklistStepReachable(steps, step.code));
}

/** Forward only after the current row is done; otherwise Save/Continue is the next action. */
export function nextChecklistStepAfter(
  steps: ChecklistStepProgress[],
  currentCode: string,
): ChecklistStepProgress | undefined {
  const index = steps.findIndex((step) => step.code === currentCode);
  if (index < 0) return pickNextChecklistStep(steps);
  const current = steps[index];
  const canAdvance =
    current.status === 'complete' ||
    current.status === 'submitted' ||
    current.status === 'waiting_on_us';
  if (!canAdvance) return undefined;
  return pickNextChecklistStep(steps.slice(index + 1));
}

export function checklistStepDisplayStatus(
  step: ChecklistStepProgress,
  nextStepCode?: string,
): ChecklistStepStatus | 'locked' {
  if (step.code === nextStepCode && step.status === 'blocked') return 'not_started';
  if (step.status === 'blocked') return 'locked';
  return step.status;
}

const AUTH_LINEAR_CODES = ['business-details', 'commercial-terms', 'beat-pay-setup'] as const;

/** Neighbor rows for Previous/Next on SCR-MER-003–008. */
export function authLinearChecklistSteps(currentCode: string): ChecklistStepProgress[] {
  const idx = AUTH_LINEAR_CODES.indexOf(currentCode as (typeof AUTH_LINEAR_CODES)[number]);
  return AUTH_LINEAR_CODES.map((code, i) => {
    const status: ChecklistStepStatus =
      idx < 0 ? 'not_started' : i < idx ? 'complete' : i === idx ? 'in_progress' : 'not_started';
    return buildChecklistStep(code, status, []);
  });
}

export const BRANCH_SETUP_STEP_CODES = new Set([
  'create-branch',
  'opening-hours',
  'service-area',
  'fulfilment-settings',
  'catalogue',
]);

/** Next branch or catalogue step during onboarding setup (SCR-MER-011–022). */
export function pickNextBranchSetupStep(
  steps: ChecklistStepProgress[],
): ChecklistStepProgress | undefined {
  return steps.find(
    (step) =>
      BRANCH_SETUP_STEP_CODES.has(step.code) &&
      step.status !== 'complete' &&
      step.status !== 'waiting_on_us' &&
      step.status !== 'blocked',
  );
}

export function summarizeGoLiveBlocker(steps: ChecklistStepProgress[]): string | null {
  const goLive = steps.find((step) => step.code === 'go-live');
  if (goLive?.status === 'complete') return null;
  if (goLive?.blockers?.[0]) return goLive.blockers[0] ?? null;
  const firstIncomplete = steps.find(
    (step) => step.status !== 'complete' && step.code !== 'go-live',
  );
  return firstIncomplete ? `${firstIncomplete.label} still needs attention` : null;
}

/** Shared polling interval for in-flight application and BeatQ Pay status. */
export function onboardingPollInterval(): number {
  return ONBOARDING_POLL_INTERVAL_MS;
}

/** 10-03 / 10-05a step-up — ERR-AUTH-0051 mapped by the shared error catalogue. */
export function isStepUpRequiredError(error: unknown): boolean {
  if (error == null) return false;
  const apiErr = error as { error?: { code?: string }; code?: string; needsMfa?: boolean };
  if (apiErr.needsMfa) return true;
  const code = (apiErr.error?.code ?? apiErr.code ?? '').toUpperCase();
  if (code === 'ERR-AUTH-0051') return true;
  const message = error instanceof Error ? error.message : String(error);
  return (
    /extra confirmation is required/i.test(message) || /recent re-authentication/i.test(message)
  );
}

function rethrowMerchantStepUp(err: unknown, challengeToken: string | null): never {
  const rec = err as { code?: string; stepUp?: unknown; error?: unknown };
  const next = new Error(
    err instanceof Error ? err.message : 'This action requires recent re-authentication.',
  ) as Error & { code?: string; needsMfa?: boolean; stepUp?: unknown; error?: unknown };
  next.code = rec.code ?? 'ERR-AUTH-0051';
  next.needsMfa = true;
  next.stepUp = challengeToken ? { challengeToken } : (rec.stepUp ?? extractStepUpChallenge(err));
  next.error = rec.error ?? rec;
  throw next;
}

/** After ERR-AUTH-0051, complete POST /merchant/v1/auth/mfa/verify and replay. */
export async function withMerchantStepUp<T>(
  action: () => Promise<T>,
  mfaCode?: string,
): Promise<T> {
  try {
    return await action();
  } catch (err) {
    if (!isStepUpRequiredError(err)) throw err;
    const challengeToken = extractStepUpChallenge(err);
    const code = mfaCode?.trim() ?? '';
    if (!challengeToken || !code) {
      rethrowMerchantStepUp(err, challengeToken);
    }
    await verifyMerchantMfa(code, challengeToken);
    return action();
  }
}

/**
 * 07-01 four-state for write perms that do not map to `.read` (`.submit`, `.accept`).
 * Viewers with the sibling read perm still see key data; step-up stays present and disabled.
 */
export type OnboardingActionAccess = 'allow' | 'disabled' | 'readonly' | 'hidden';

export function resolveOnboardingActionAccess(
  writeState: string,
  readState: string,
): OnboardingActionAccess {
  if (writeState === 'editable') return 'allow';
  if (writeState === 'disabled') return 'disabled';
  if (writeState === 'readonly') return 'readonly';
  if (readState === 'editable' || readState === 'readonly' || readState === 'disabled') {
    return 'readonly';
  }
  return 'hidden';
}

export type ChecklistCompletionStats = {
  completed: number;
  total: number;
  percent: number;
  remainingMinutes: number;
};

/** Progress summary for checklist header (07-05 §16). */
export function checklistCompletionStats(steps: ChecklistStepProgress[]): ChecklistCompletionStats {
  const total = steps.length;
  const completed = steps.filter((step) => step.status === 'complete').length;
  const percent = total > 0 ? Math.round((completed / total) * 100) : 0;
  const remainingMinutes = steps.reduce(
    (sum, step) => (step.status === 'complete' ? sum : sum + (step.estimateMinutes ?? 0)),
    0,
  );
  return { completed, total, percent, remainingMinutes };
}

/** Human-readable application status for UI (not raw enums). */
export function humanizeApplicationStatus(status?: string | null): string {
  if (!status?.trim()) return 'In progress';
  return status
    .trim()
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export type ChecklistBadgeVariant = 'default' | 'success' | 'warning' | 'danger' | 'info';

/** Maps checklist step status to Badge variant. */
export function checklistStatusBadgeVariant(
  status: ChecklistStepStatus | 'locked',
): ChecklistBadgeVariant {
  switch (status) {
    case 'complete':
      return 'success';
    case 'blocked':
    case 'needs-attention':
      return 'danger';
    case 'locked':
      return 'default';
    case 'waiting_on_us':
      return 'info';
    case 'submitted':
      return 'info';
    case 'in_progress':
      return 'warning';
    default:
      return 'default';
  }
}

function buildChecklistStep(
  code: string,
  status: ChecklistStepStatus,
  blockers: string[],
  routeOverride?: string,
): ChecklistStepProgress {
  const meta = ONBOARDING_CHECKLIST_STEPS.find((step) => step.code === code)!;
  return {
    code: meta.code,
    label: meta.label,
    route: routeOverride ?? meta.route,
    status,
    blockers,
    estimateMinutes: CHECKLIST_STEP_ESTIMATE_MINUTES[code],
  };
}

function checklistRouteForApplication(
  stepCode: string,
  baseRoute: string,
  application?: OnboardingApplication,
): string {
  const applicationId = application?.applicationId;
  if (!applicationId || stepCode !== 'business-details') return baseRoute;
  return `/business-details?applicationId=${encodeURIComponent(applicationId)}`;
}

function humanizeKey(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function normalizeStepCode(stepKeyOrCode?: string): string | undefined {
  if (!stepKeyOrCode) return undefined;
  return STEP_KEY_TO_CHECKLIST[stepKeyOrCode] ?? stepKeyOrCode;
}

export function applicationFromEnvelope(envelope: unknown): OnboardingApplication | undefined {
  const nested = readDataFromEnvelope<Record<string, unknown>>(envelope);
  const raw =
    nested && typeof nested === 'object'
      ? nested
      : envelope && typeof envelope === 'object' && !Array.isArray(envelope)
        ? (envelope as Record<string, unknown>)
        : undefined;
  if (!raw || typeof raw !== 'object') return undefined;

  const applicationId =
    (typeof raw.applicationId === 'string' && raw.applicationId) ||
    (typeof raw.id === 'string' && raw.id) ||
    undefined;
  const resumeToken = typeof raw.resumeToken === 'string' ? raw.resumeToken : undefined;
  const status = typeof raw.status === 'string' ? raw.status : undefined;
  const blockingReasons = Array.isArray(raw.blockingReasons)
    ? (raw.blockingReasons as Array<{ stepKey?: string; reason?: string }>)
    : [];

  const rawSteps = Array.isArray(raw.steps) ? raw.steps : [];
  const steps = rawSteps.map((step) => {
    if (typeof step === 'string') {
      const code = normalizeStepCode(step) ?? step;
      return {
        code,
        label: humanizeKey(step),
        status: 'PENDING' as string,
        blockers: [] as string[],
      };
    }
    const s = step as {
      stepKey?: string;
      code?: string;
      label?: string;
      status?: string;
      blockers?: string[];
    };
    const code = normalizeStepCode(s.stepKey ?? s.code) ?? s.code ?? s.stepKey;
    const blockers = [
      ...(s.blockers ?? []),
      ...blockingReasons
        .filter((b) => b.stepKey === s.stepKey)
        .map((b) => b.reason)
        .filter((r): r is string => Boolean(r)),
    ];
    return {
      stepKey: s.stepKey,
      code,
      label: s.label ?? (s.stepKey ? humanizeKey(s.stepKey) : code),
      status: s.status,
      blockers,
    };
  });

  return {
    applicationId,
    resumeToken,
    status,
    businessId: typeof raw.businessId === 'string' ? raw.businessId : undefined,
    draftPayload:
      raw.draftPayload && typeof raw.draftPayload === 'object'
        ? (raw.draftPayload as Record<string, unknown>)
        : undefined,
    steps,
    estimatedGoLiveAt:
      typeof raw.estimatedGoLiveAt === 'string' || raw.estimatedGoLiveAt === null
        ? (raw.estimatedGoLiveAt as string | null)
        : null,
    blockingReasons,
  };
}

function readAddressFields(value: unknown): { line1?: string; postcode?: string } {
  const rec = asRecord(value);
  if (!rec) return {};
  const line1 =
    asString(rec.line1) ??
    asString(rec.addressLine1) ??
    asString(rec.registeredAddressLine1) ??
    asString(rec.building) ??
    undefined;
  const postcode = asString(rec.postcode) ?? undefined;
  return { line1, postcode };
}

function readMoneyMinor(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const rec = asRecord(value);
  if (!rec) return undefined;
  if (typeof rec.amountMinor === 'number' && Number.isFinite(rec.amountMinor)) {
    return rec.amountMinor;
  }
  if (typeof rec.minor === 'number' && Number.isFinite(rec.minor)) return rec.minor;
  return undefined;
}

/** GET /business — flatten 10-05a nested address + companiesHouseNumber alias. */
export function businessFromEnvelope(envelope: unknown): MerchantBusiness | undefined {
  const raw =
    readObjectFieldFromEnvelope<Record<string, unknown>>(envelope, ['business']) ??
    readDataFromEnvelope<Record<string, unknown>>(envelope);
  if (!raw || typeof raw !== 'object') return undefined;

  const nestedBusiness = asRecord(raw.business);
  const record =
    nestedBusiness && !asString(raw.legalName) && !asString(raw.id) ? nestedBusiness : raw;
  const registered = readAddressFields(record.registeredAddress);
  const trading = readAddressFields(record.tradingAddress);
  const companyNumber = asString(record.companyNumber) ?? asString(record.companiesHouseNumber);

  return {
    id: asString(record.id) ?? undefined,
    legalName: asString(record.legalName) ?? undefined,
    tradingName: asString(record.tradingName) ?? undefined,
    billingEmail: asString(record.billingEmail) ?? undefined,
    status: asString(record.status) ?? undefined,
    postcode: asString(record.postcode) ?? registered.postcode ?? null,
    companyNumber: companyNumber ?? null,
    description: asString(record.description) ?? null,
    supportPhoneE164: asString(record.supportPhoneE164) ?? null,
    vatNumber: asString(record.vatNumber) ?? null,
    vatRegistered: record.vatRegistered === true,
    entityType: asString(record.entityType) ?? asString(record.companyType) ?? null,
    registeredAddressLine1: asString(record.registeredAddressLine1) ?? registered.line1 ?? null,
    registeredPostcode: asString(record.registeredPostcode) ?? registered.postcode ?? null,
    tradingAddressLine1: asString(record.tradingAddressLine1) ?? trading.line1 ?? null,
    tradingPostcode: asString(record.tradingPostcode) ?? trading.postcode ?? null,
  };
}

function normalizeCommercialTerms(raw: Record<string, unknown>): CommercialTermsVersion {
  const commission =
    typeof raw.commissionBps === 'number'
      ? raw.commissionBps
      : typeof raw.commissionRateBps === 'number'
        ? raw.commissionRateBps
        : undefined;
  return {
    id: typeof raw.id === 'string' ? raw.id : undefined,
    version: typeof raw.version === 'string' ? raw.version : undefined,
    commissionBps: commission,
    serviceFeeRateBps:
      typeof raw.serviceFeeRateBps === 'number' ? raw.serviceFeeRateBps : undefined,
    instantPayoutFeeMinor: readMoneyMinor(raw.instantPayoutFee ?? raw.instantPayoutFeeMinor),
    smallOrderFeeMinor:
      typeof raw.smallOrderFeeMinor === 'number' ? raw.smallOrderFeeMinor : undefined,
    refundHandling: typeof raw.refundHandling === 'string' ? raw.refundHandling : undefined,
    commissionModel: typeof raw.commissionModel === 'string' ? raw.commissionModel : undefined,
    effectiveFrom: typeof raw.effectiveFrom === 'string' ? raw.effectiveFrom : undefined,
    acceptBy:
      typeof raw.acceptBy === 'string'
        ? raw.acceptBy
        : typeof raw.effectiveTo === 'string'
          ? raw.effectiveTo
          : null,
    pending: !raw.acceptedAt,
    acceptedAt: typeof raw.acceptedAt === 'string' ? raw.acceptedAt : null,
  };
}

export function commercialTermsFromEnvelope(envelope: unknown): CommercialTermsVersion[] {
  const data = readDataFromEnvelope<Record<string, unknown>>(envelope);
  const termsField = data?.terms ?? readObjectFieldFromEnvelope(envelope, ['terms']);
  if (!termsField) return [];
  if (Array.isArray(termsField)) {
    return termsField
      .filter((t): t is Record<string, unknown> => Boolean(t) && typeof t === 'object')
      .map((t) => normalizeCommercialTerms(t));
  }
  if (typeof termsField === 'object') {
    return [normalizeCommercialTerms(termsField as Record<string, unknown>)];
  }
  return [];
}

export type SetupProgressInput = {
  branch?: {
    branchId?: string;
    status?: string;
    supportsDelivery?: boolean;
    supportsCollection?: boolean;
    defaultPrepMinutes?: number | null;
  };
  openingHoursCount?: number;
  hasServiceArea?: boolean;
  publishedProductCount?: number;
  activationBlockers?: Array<{ requirement?: string; reason?: string }>;
};

/** Checklist rows for an in-flight MOD-005 application (draft / pre-tenant). */
export function resolveApplicationChecklistSteps(
  application?: OnboardingApplication,
): ChecklistStepProgress[] {
  const backendSteps = application?.steps ?? [];
  const blockingReasons = application?.blockingReasons ?? [];
  const preTenant = !application?.businessId;

  return ONBOARDING_CHECKLIST_STEPS.map((step) => {
    if (preTenant && DRAFT_LOCKED_CHECKLIST_CODES.has(step.code)) {
      return buildChecklistStep(step.code, 'blocked', [
        'Available after you save business details',
      ]);
    }
    const primaryKey = CHECKLIST_PRIMARY_STEP_KEY[step.code];
    const backendStep = primaryKey
      ? backendSteps.find((row) => row.stepKey === primaryKey)
      : undefined;
    const blockers =
      backendStep?.status === 'COMPLETED'
        ? []
        : [
            ...new Set([
              ...(backendStep?.blockers ?? []),
              ...blockingReasons
                .filter((reason) => reason.stepKey === primaryKey)
                .map((reason) => reason.reason)
                .filter((reason): reason is string => Boolean(reason)),
            ]),
          ];
    const status = resolveApplicationStepStatus(
      step.code,
      application,
      backendStep?.status,
      blockers,
    );

    return buildChecklistStep(
      step.code,
      status,
      blockers,
      checklistRouteForApplication(step.code, step.route, application),
    );
  });
}

/** Tenant-progress checklist when no MOD-005 application id is present (post–go-live path). */
export function deriveTenantChecklistProgress(input: {
  business?: MerchantBusiness;
  beatPay?: BeatPayStatusResponse;
  terms?: CommercialTermsVersion[];
  setup?: SetupProgressInput;
}): ChecklistStepProgress[] {
  const businessDone = Boolean(input.business?.tradingName && input.business?.billingEmail);
  const beatPayStatus = input.beatPay?.status ?? 'NOT_STARTED';
  const beatPayDone = beatPayIsReady(input.beatPay);
  const termsRow = input.terms?.[0];
  const termsDone = Boolean(termsRow && !termsRow.pending && termsRow.acceptedAt);

  const setup = input.setup;
  const branchDone = Boolean(setup?.branch?.branchId);
  const hoursDone = branchDone && (setup?.openingHoursCount ?? 0) > 0;
  const deliveryOn = setup?.branch?.supportsDelivery === true;
  const areaDone = branchDone && (deliveryOn ? Boolean(setup?.hasServiceArea) : true);
  const fulfilmentDone =
    branchDone &&
    Boolean(setup?.branch?.supportsDelivery || setup?.branch?.supportsCollection) &&
    Number(setup?.branch?.defaultPrepMinutes) > 0;
  const catalogueDone = (setup?.publishedProductCount ?? 0) > 0;
  const liveDone = setup?.branch?.status === 'ACTIVE';
  const activationReasons = (setup?.activationBlockers ?? [])
    .map((blocker) => blocker.reason)
    .filter((reason): reason is string => Boolean(reason));

  const businessBlockers = businessDone ? [] : ['Trading name and billing email required'];
  const beatPayBlockers = beatPayDone
    ? []
    : beatPayStatus === 'ACTIVE'
      ? ['BeatQ Pay is active for payments but its payout destination is not ready']
      : beatPayStatus === 'PENDING_VERIFICATION'
        ? ['BeatQ Pay is reviewing your details']
        : beatPayStatus === 'PENDING_ONBOARDING'
          ? ['Finish the required steps with our payment partner']
          : beatPayStatus === 'FAILED'
            ? ['BeatQ Pay setup needs attention']
            : ['Set up BeatQ Pay to accept payments and receive payouts'];

  return [
    buildChecklistStep(
      'business-details',
      businessDone ? 'complete' : businessBlockers.length ? 'blocked' : 'not_started',
      businessBlockers,
    ),
    buildChecklistStep(
      'beat-pay-setup',
      beatPayDone
        ? 'complete'
        : beatPayStatus === 'PENDING_VERIFICATION'
          ? 'waiting_on_us'
          : beatPayStatus === 'NOT_STARTED'
            ? 'not_started'
            : 'in_progress',
      beatPayBlockers,
    ),
    buildChecklistStep(
      'commercial-terms',
      termsDone ? 'complete' : 'not_started',
      termsDone ? [] : ['Commercial terms not accepted'],
    ),
    buildChecklistStep('create-branch', branchDone ? 'complete' : 'not_started', [
      'Create your first branch to continue setup',
    ]),
    buildChecklistStep(
      'fulfilment-settings',
      fulfilmentDone ? 'complete' : branchDone ? 'not_started' : 'blocked',
      [branchDone ? 'Choose fulfilment methods and set prep time' : 'Create a branch first'],
    ),
    buildChecklistStep(
      'opening-hours',
      hoursDone ? 'complete' : branchDone ? 'not_started' : 'blocked',
      [branchDone ? 'Set the hours this branch trades' : 'Create a branch first'],
    ),
    buildChecklistStep(
      'service-area',
      areaDone ? 'complete' : branchDone ? 'not_started' : 'blocked',
      [
        branchDone
          ? 'Define a delivery service area, or turn delivery off'
          : 'Create a branch first',
      ],
    ),
    buildChecklistStep(
      'catalogue',
      catalogueDone ? 'complete' : branchDone ? 'not_started' : 'blocked',
      ['Publish at least one product'],
    ),
    buildChecklistStep(
      'go-live',
      liveDone ? 'complete' : 'blocked',
      activationReasons.length > 0
        ? activationReasons
        : ['Complete the remaining setup steps before going live'],
    ),
  ];
}

export async function startOnboardingApplication(body: {
  verticalCode: string;
  companyType?: string;
  channel?: string;
}) {
  const client = getMerchantClient();
  const { data, error, response } = await client.POST('/merchant/v1/onboarding/applications', {
    body: {
      verticalCode: body.verticalCode,
      companyType: body.companyType,
      channel: body.channel,
    } as never,
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function patchOnboardingApplication(
  applicationId: string,
  patch: Record<string, unknown>,
  resumeToken?: string | null,
) {
  const client = getMerchantClient();
  const { data, error, response } = await client.PATCH(
    '/merchant/v1/onboarding/applications/{id}',
    {
      params: { path: { id: applicationId } },
      body: patch as never,
      headers: resumeToken ? { 'X-Onboarding-Token': resumeToken } : undefined,
    },
  );
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function getBranchActivationReadiness(branchId: string) {
  const client = getMerchantClient();
  const { data, error, response } = await client.GET(
    '/merchant/v1/branches/{id}/activation-readiness',
    { params: { path: { id: branchId } } },
  );
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export function activationReadinessFromEnvelope(envelope: unknown): {
  ready: boolean;
  blockers: Array<{ requirement?: string; reason?: string }>;
} {
  const data = readDataFromEnvelope<Record<string, unknown>>(envelope);
  return {
    ready: Boolean(data?.ready),
    blockers: Array.isArray(data?.blockers)
      ? (data.blockers as Array<{ requirement?: string; reason?: string }>)
      : [],
  };
}

/** Creates the Business, opens the verification case and binds the owner's role. */
export async function submitOnboardingApplication(
  applicationId: string,
  resumeToken?: string | null,
) {
  const client = getMerchantClient();
  const { data, error, response } = await client.POST(
    '/merchant/v1/onboarding/applications/{id}/submit',
    {
      params: { path: { id: applicationId } },
      body: {} as never,
      headers: resumeToken ? { 'X-Onboarding-Token': resumeToken } : undefined,
    },
  );
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function getOnboardingApplication(applicationId: string, resumeToken?: string | null) {
  const client = getMerchantClient();
  const { data, error, response } = await client.GET('/merchant/v1/onboarding/applications/{id}', {
    params: { path: { id: applicationId } },
    headers: resumeToken ? { 'X-Onboarding-Token': resumeToken } : undefined,
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function getBusiness() {
  const client = getMerchantClient();
  const { data, error, response } = await client.GET('/merchant/v1/business');
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function updateBusiness(body: {
  tradingName?: string;
  description?: string;
  supportPhoneE164?: string;
  billingEmail?: string;
}) {
  const client = getMerchantClient();
  const { data, error, response } = await client.PATCH('/merchant/v1/business', {
    body: body as never,
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function getCommercialTerms() {
  const client = getMerchantClient();
  const { data, error, response } = await client.GET('/merchant/v1/business/commercial-terms');
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export type MerchantOnboardingProgress = {
  applicationId?: string | null;
  applicationStatus?: string | null;
  applicationState?: 'DRAFT' | 'AWAITING_BEAT_PAY' | 'COMPLETED' | 'REJECTED' | null;
  businessId?: string;
  beatPay?: BeatPayStatusResponse;
  phases: Array<{
    key: string;
    label: string;
    status?: string;
    tasks: Array<{
      key: string;
      label: string;
      status?: string;
      blockers: Array<{ requirement?: string; reason?: string; branchId?: string }>;
    }>;
  }>;
  onboardingComplete?: boolean;
  operationallyLive?: boolean;
  activationBlockers: Array<{ requirement?: string; reason?: string; branchId?: string }>;
};

export function onboardingProgressFromEnvelope(
  envelope: unknown,
): MerchantOnboardingProgress | null {
  const raw = readDataFromEnvelope<Record<string, unknown>>(envelope);
  if (!raw || !Array.isArray(raw.phases)) return null;
  const beatPayRaw = asRecord(raw.beatPay);
  const beatPayStatus = asString(beatPayRaw?.status) as BeatPayStatus | null;
  const phases = raw.phases
    .filter(
      (phase): phase is Record<string, unknown> => Boolean(phase) && typeof phase === 'object',
    )
    .map((phase) => ({
      key: asString(phase.key) ?? '',
      label: asString(phase.label) ?? '',
      status: asString(phase.status) ?? undefined,
      tasks: (Array.isArray(phase.tasks) ? phase.tasks : [])
        .filter(
          (task): task is Record<string, unknown> => Boolean(task) && typeof task === 'object',
        )
        .map((task) => ({
          key: asString(task.key) ?? '',
          label: asString(task.label) ?? '',
          status: asString(task.status) ?? undefined,
          blockers: (Array.isArray(task.blockers) ? task.blockers : []).filter(
            (blocker): blocker is { requirement?: string; reason?: string; branchId?: string } =>
              Boolean(blocker) && typeof blocker === 'object',
          ),
        })),
    }));
  return {
    applicationId: asString(raw.applicationId) ?? null,
    applicationStatus: asString(raw.applicationStatus) ?? null,
    applicationState:
      (asString(raw.applicationState) as MerchantOnboardingProgress['applicationState']) ?? null,
    businessId: asString(raw.businessId) ?? undefined,
    beatPay:
      beatPayRaw && beatPayStatus
        ? {
            status: beatPayStatus,
            canAcceptPayments: beatPayRaw.canAcceptPayments === true,
            canReceivePayouts: beatPayRaw.canReceivePayouts === true,
            ready: beatPayRaw.ready === true,
            capabilities: asRecord(beatPayRaw.capabilities),
            requirements: Array.isArray(beatPayRaw.requirements)
              ? (beatPayRaw.requirements as BeatPayCapabilityRequirement[])
              : [],
            accountHolderId: asString(beatPayRaw.accountHolderId),
            legalEntityId: asString(beatPayRaw.legalEntityId),
            balanceAccountId: asString(beatPayRaw.balanceAccountId),
            transferInstrumentId: asString(beatPayRaw.transferInstrumentId),
          }
        : undefined,
    phases,
    onboardingComplete: raw.onboardingComplete === true,
    operationallyLive: raw.operationallyLive === true,
    activationBlockers: (Array.isArray(raw.activationBlockers)
      ? raw.activationBlockers
      : []
    ).filter(
      (blocker): blocker is { requirement?: string; reason?: string; branchId?: string } =>
        Boolean(blocker) && typeof blocker === 'object',
    ),
  };
}

const PROGRESS_TASK_TO_STEPS: Record<string, readonly string[]> = {
  BUSINESS_DETAILS: ['business-details'],
  TERMS: ['commercial-terms'],
  BEAT_PAY_START: ['beat-pay-setup'],
  PAYMENTS: ['beat-pay-setup'],
  PAYOUTS: ['beat-pay-setup'],
  BEAT_PAY_READY: ['beat-pay-setup'],
  BRANCH: ['create-branch'],
  BRANCH_OPERATIONS: ['fulfilment-settings', 'opening-hours', 'service-area'],
  FULFILMENT: ['fulfilment-settings'],
  FULFILMENT_SETTINGS: ['fulfilment-settings'],
  OPENING_HOURS: ['opening-hours'],
  SERVICE_AREA: ['service-area'],
  CATALOGUE: ['catalogue'],
  GO_LIVE: ['go-live'],
};

/** Overlay authoritative composed progress while retaining granular route-level fallback rows. */
export function mergeOnboardingProgress(
  fallback: ChecklistStepProgress[],
  progress?: MerchantOnboardingProgress | null,
): ChecklistStepProgress[] {
  if (!progress) return fallback;
  const tasks = progress.phases.flatMap((phase) => phase.tasks);
  return fallback.map((step) => {
    const matching = tasks.filter((task) => PROGRESS_TASK_TO_STEPS[task.key]?.includes(step.code));
    if (matching.length === 0) return step;
    const blockers = matching.flatMap((task) =>
      task.blockers.map((blocker) => blocker.reason).filter((reason): reason is string => !!reason),
    );
    const allComplete = matching.every((task) => task.status === 'COMPLETED');
    const anyComplete = matching.some((task) => task.status === 'COMPLETED');
    const anyBlocked = matching.some((task) => task.status === 'BLOCKED');
    let status: ChecklistStepStatus = step.status;
    if (allComplete) status = 'complete';
    else if (
      step.code === 'beat-pay-setup' &&
      progress.beatPay?.status === 'PENDING_VERIFICATION'
    ) {
      status = 'waiting_on_us';
    } else if (step.code === 'beat-pay-setup' && progress.beatPay?.status === 'FAILED') {
      status = 'needs-attention';
    } else if (anyBlocked) status = 'needs-attention';
    else if (anyComplete) status = 'in_progress';
    return { ...step, status, blockers: blockers.length > 0 ? blockers : step.blockers };
  });
}

export async function acceptCommercialTerms(
  termsId: string,
  body?: { confirmUnderstanding?: boolean; acceptedVersion?: string },
) {
  const client = getMerchantClient();
  const { data, error, response } = await client.POST(
    '/merchant/v1/business/commercial-terms/{id}/accept',
    {
      params: { path: { id: termsId } },
      body: {
        confirmUnderstanding: body?.confirmUnderstanding ?? true,
        acceptedVersion: body?.acceptedVersion ?? termsId,
      } as never,
    },
  );
  if (!response.ok || error) throwMerchantSdkError(error);
  return data;
}

// ─── BeatQ Pay (Adyen Hosted Onboarding) ─────────────────────────────

function merchantApiUrl(path: string): string {
  const base = getApiBaseUrl().replace(/\/$/, '');
  return `${base}${path.startsWith('/') ? path : `/${path}`}`;
}

async function merchantFetch(path: string, init?: RequestInit) {
  const token = getAccessToken();
  const response = await fetch(merchantApiUrl(path), {
    credentials: 'include',
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init?.headers ?? {}),
    },
  });
  const json = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(sdkErrorMessage(json));
  return json;
}

/**
 * New grouped progress contract. Older APIs and roles without the composed-progress
 * permission return null so the existing per-resource checklist remains usable.
 */
export async function getMerchantOnboardingProgress(): Promise<MerchantOnboardingProgress | null> {
  const token = getAccessToken();
  const response = await fetch(merchantApiUrl('/merchant/v1/onboarding/progress'), {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  const json = await response.json().catch(() => ({}));
  if (response.status === 403 || response.status === 404 || response.status === 405) return null;
  if (!response.ok) throw new Error(sdkErrorMessage(json));
  return onboardingProgressFromEnvelope(json);
}

export type BeatPayStatus =
  'NOT_STARTED' | 'PENDING_ONBOARDING' | 'PENDING_VERIFICATION' | 'ACTIVE' | 'FAILED';

export interface BeatPayCapabilityRequirement {
  key: string;
  mandatory: true;
  satisfied: boolean;
  evidenceState: 'PENDING' | 'ACCEPTED' | 'REJECTED';
  messages: string[];
}

export interface BeatPayStatusResponse {
  status: BeatPayStatus;
  canAcceptPayments: boolean;
  canReceivePayouts: boolean;
  ready?: boolean;
  capabilities: Record<string, unknown> | null;
  requirements?: BeatPayCapabilityRequirement[];
  accountHolderId: string | null;
  legalEntityId: string | null;
  balanceAccountId: string | null;
  transferInstrumentId: string | null;
}

export function beatPayIsReady(
  beatPay?: Pick<
    BeatPayStatusResponse,
    'status' | 'canAcceptPayments' | 'canReceivePayouts'
  > | null,
): boolean {
  return (
    beatPay?.status === 'ACTIVE' &&
    beatPay.canAcceptPayments === true &&
    beatPay.canReceivePayouts === true
  );
}

export async function getBeatPayStatus(options?: {
  refresh?: boolean;
}): Promise<BeatPayStatusResponse> {
  const qs = options?.refresh ? '?refresh=1' : '';
  const json = await merchantFetch(`/merchant/v1/onboarding/beat-pay/status${qs}`);
  return readEntityFromEnvelope<BeatPayStatusResponse>(json) ?? (json as BeatPayStatusResponse);
}

export async function refreshBeatPayStatus(): Promise<BeatPayStatusResponse> {
  const json = await merchantFetch('/merchant/v1/onboarding/beat-pay/refresh', {
    method: 'POST',
    body: '{}',
  });
  return readEntityFromEnvelope<BeatPayStatusResponse>(json) ?? (json as BeatPayStatusResponse);
}

export async function startBeatPayOnboarding(): Promise<BeatPayStatusResponse> {
  const json = await merchantFetch('/merchant/v1/onboarding/beat-pay/start', {
    method: 'POST',
    body: '{}',
  });
  return readEntityFromEnvelope<BeatPayStatusResponse>(json) ?? (json as BeatPayStatusResponse);
}

export async function getBeatPayOnboardingLink(): Promise<{ redirectUrl: string }> {
  const json = await merchantFetch('/merchant/v1/onboarding/beat-pay/link', {
    method: 'POST',
    body: '{}',
  });
  return readEntityFromEnvelope<{ redirectUrl: string }>(json) ?? (json as { redirectUrl: string });
}
